/**
 * Popover — headless primitive wrapping Radix Popover.
 *
 * Radix's Popover owns focus management, collision detection, portal,
 * ESC-to-close, and click-outside behavior (ARCHITECTURE §6). We wrap
 * thinly:
 *   - Permission gating on <Root> (denied → null + permission:denied
 *     audit; any mode collapses to null for a popover that has no useful
 *     visible-but-inert state)
 *   - Audit events on open / close
 *   - `data-oshon-primitive="popover-*"` styling hooks
 *
 * "use client" mandatory — Radix uses hooks + DOM APIs internally.
 */

'use client';

import * as RadixPopover from '@radix-ui/react-popover';
import { forwardRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

// ────────────────────────────────────────────────────────────────────────────
// Root

export interface PopoverRootProps
  extends Omit<RadixPopover.PopoverProps, 'onOpenChange'> {
  /**
   * Fires whenever the popover's open state changes. The primitive fires
   * `popover:open` / `popover:close` audit events automatically before
   * this callback runs.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. Denied + any mode returns `null` (popovers, like
   * dialogs, have no useful visible-but-inert state) and emits a
   * `permission:denied` audit.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name for `permissions.can('view', resource, attrs)`.
   * Default `'popover'`. Override for specific semantics, e.g.
   * `resource="record:quick-actions"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
  children: ReactNode;
}

/**
 * Popover root. Controls open state, gates on permissions, fires audit
 * events. Must wrap every other `Popover.*` sub-component.
 */
function Root({
  children,
  open,
  defaultOpen,
  onOpenChange,
  permissions,
  resource = 'popover',
  permissionAttrs,
  modal,
}: PopoverRootProps) {
  const context = usePermissions();
  const effective = resolvePermissions(permissions, context);
  const emit = useAudit();

  const { granted, denyReason } = evaluatePermission(
    effective,
    'view',
    resource,
    permissionAttrs,
  );

  if (!granted) {
    emit({
      component: 'popover',
      action: CANONICAL_ACTIONS.PERMISSION_DENIED,
      permissionResult: 'denied',
      ...(denyReason ? { denyReason } : {}),
      details: { attemptedAction: 'view', resource },
    });
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    emit({
      component: 'popover',
      action: next
        ? CANONICAL_ACTIONS.POPOVER_OPEN
        : CANONICAL_ACTIONS.POPOVER_CLOSE,
      permissionResult: 'granted',
    });
    onOpenChange?.(next);
  };

  return (
    <RadixPopover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      {children}
    </RadixPopover.Root>
  );
}
Root.displayName = 'Popover.Root';

// ────────────────────────────────────────────────────────────────────────────
// Trigger

export type PopoverTriggerProps = ComponentPropsWithoutRef<typeof RadixPopover.Trigger>;

const Trigger = forwardRef<
  ElementRef<typeof RadixPopover.Trigger>,
  PopoverTriggerProps
>(function PopoverTrigger(props, ref) {
  return (
    <RadixPopover.Trigger
      ref={ref}
      data-oshon-primitive="popover-trigger"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Anchor

export type PopoverAnchorProps = ComponentPropsWithoutRef<typeof RadixPopover.Anchor>;

/**
 * Anchor. Optional — when present, positions the popover relative to an
 * element separate from the trigger. Useful for context menus anchored
 * to a cursor position or cell.
 */
const Anchor = forwardRef<
  ElementRef<typeof RadixPopover.Anchor>,
  PopoverAnchorProps
>(function PopoverAnchor(props, ref) {
  return (
    <RadixPopover.Anchor
      ref={ref}
      data-oshon-primitive="popover-anchor"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Portal

export type PopoverPortalProps = RadixPopover.PopoverPortalProps;

function Portal(props: PopoverPortalProps) {
  return <RadixPopover.Portal {...props} />;
}
Portal.displayName = 'Popover.Portal';

// ────────────────────────────────────────────────────────────────────────────
// Content

export type PopoverContentProps = ComponentPropsWithoutRef<typeof RadixPopover.Content>;

/**
 * Content. Radix handles positioning, collision, focus return, ESC-to-close,
 * click-outside, and `role="dialog"` for non-modal popovers. We add only
 * the styling hook.
 */
const Content = forwardRef<
  ElementRef<typeof RadixPopover.Content>,
  PopoverContentProps
>(function PopoverContent(props, ref) {
  return (
    <RadixPopover.Content
      ref={ref}
      data-oshon-primitive="popover-content"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Close

export type PopoverCloseProps = ComponentPropsWithoutRef<typeof RadixPopover.Close>;

const Close = forwardRef<
  ElementRef<typeof RadixPopover.Close>,
  PopoverCloseProps
>(function PopoverClose(props, ref) {
  return (
    <RadixPopover.Close
      ref={ref}
      data-oshon-primitive="popover-close"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Arrow

export type PopoverArrowProps = ComponentPropsWithoutRef<typeof RadixPopover.Arrow>;

const Arrow = forwardRef<
  ElementRef<typeof RadixPopover.Arrow>,
  PopoverArrowProps
>(function PopoverArrow(props, ref) {
  return (
    <RadixPopover.Arrow
      ref={ref}
      data-oshon-primitive="popover-arrow"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Compound export

/**
 * Compound Popover primitive. Use as:
 *
 *   <Popover.Root>
 *     <Popover.Trigger>Open</Popover.Trigger>
 *     <Popover.Portal>
 *       <Popover.Content>
 *         <p>Popover content</p>
 *         <Popover.Close>Close</Popover.Close>
 *       </Popover.Content>
 *     </Popover.Portal>
 *   </Popover.Root>
 */
export const Popover = {
  Root,
  Trigger,
  Anchor,
  Portal,
  Content,
  Close,
  Arrow,
};

export type PopoverProps = PopoverRootProps;
